import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ChevronDown, Loader2, Play } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import Seo from "@/components/Seo";
import ToolPrimer from "@/components/ToolPrimer";

const DEFAULTS = {
  price: 284,
  eps_year: 8.96,
  years: 10,
  n_sims: 300,
  growth: 0.13,
  discount: 0.12,
  lt_growth: 0.05,
  eps_var: 0.05,
  mean_reversion: 0.15,
  shock_prob: 0.05,
  shock_loss: 0.5,
  g_min: 0.104,
  g_max: 0.156,
  r_min: 0.08,
  r_max: 0.15,
  grid_size: 12,
};

const CURRENCIES = { EUR: "\u20AC", USD: "$", GBP: "\u00A3" };

export default function MonteCarloDCF() {
  const [params, setParams] = useState(DEFAULTS);
  const [currency, setCurrency] = useState("USD");
  const [advOpen, setAdvOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const cs = CURRENCIES[currency];
  const set = (k) => (v) => setParams((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tools/dcf/run", params);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulation failed");
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n, opts = {}) =>
    cs +
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: opts.decimals ?? 2,
      minimumFractionDigits: opts.decimals ?? 2,
    }).format(n);

  return (
    <main data-testid="mc-dcf-tool" className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <Seo
        title="Monte Carlo DCF"
        description="Discounted cash-flow valuation with Monte Carlo simulation of EPS paths. Distribution of intrinsic prices, sensitivity heatmap on growth × discount rate."
        path="/tools/monte-carlo-dcf"
      />
      <Link
        to="/tools"
        data-testid="mc-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-colors mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All tools
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-3">
            Financial tool · Monte Carlo DCF
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tighter">
            The market is a distribution,
            <br />
            <span className="italic text-primary">not a number.</span>
          </h1>
        </div>
        <p className="max-w-md text-sm text-foreground/60 leading-relaxed">
          Simulates thousands of EPS paths under mean-reverting growth with
          occasional shocks, discounts each one back to today, and shows the
          range of prices that assumption set could justify.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        {/* Inputs */}
        <section className="lg:col-span-4 space-y-6" data-testid="mc-inputs">
          <Panel title="Stock" side={
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="mc-currency"
              className="text-xs uppercase tracking-widest bg-background border border-border rounded-full px-3 py-1.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            >
              {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          }>
            <Field label="Current price" value={params.price} display={fmt(params.price)}
              min={1} max={2000} step={1} onChange={set("price")} testId="mc-price" />
            <Field label="Latest annual EPS" value={params.eps_year} display={fmt(params.eps_year)}
              min={0.1} max={200} step={0.01} onChange={set("eps_year")} testId="mc-eps" last />
          </Panel>

          <Panel title="Assumptions">
            <Field label="Annual EPS growth" value={params.growth}
              display={pctDisp(params.growth)} min={-0.05} max={0.4} step={0.005}
              onChange={set("growth")} testId="mc-growth" />
            <Field label="Discount rate" value={params.discount}
              display={pctDisp(params.discount)} min={0.02} max={0.25} step={0.005}
              onChange={set("discount")} testId="mc-discount" />
            <Field label="Long-term growth" value={params.lt_growth}
              display={pctDisp(params.lt_growth)} min={0} max={0.08} step={0.001}
              onChange={set("lt_growth")} testId="mc-lt-growth" />
            <Field label="Horizon" value={params.years}
              display={`${params.years} years`} min={3} max={30} step={1}
              onChange={(v) => set("years")(Math.round(v))} testId="mc-years" last />
          </Panel>

          {/* Advanced */}
          <div className="border border-border rounded-sm bg-background overflow-hidden">
            <button
              onClick={() => setAdvOpen((v) => !v)}
              data-testid="mc-advanced-toggle"
              className="w-full flex items-center justify-between px-6 py-4 text-xs uppercase tracking-[0.25em] text-foreground/70 hover:text-primary transition-colors"
            >
              Advanced · process & sensitivity
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                className={`transition-transform duration-300 ${advOpen ? "rotate-180" : ""}`}
              />
            </button>
            {advOpen && (
              <div className="px-6 pb-6 pt-2 space-y-8 border-t border-border">
                <div>
                  <SectionLabel>EPS process</SectionLabel>
                  <Field label="Monthly EPS volatility" value={params.eps_var}
                    display={pctDisp(params.eps_var)} min={0} max={0.5} step={0.005}
                    onChange={set("eps_var")} testId="mc-eps-var" />
                  <Field label="Mean reversion speed" value={params.mean_reversion}
                    display={params.mean_reversion.toFixed(2)} min={0} max={1} step={0.01}
                    onChange={set("mean_reversion")} testId="mc-b" />
                  <Field label="Shock probability (monthly)" value={params.shock_prob}
                    display={pctDisp(params.shock_prob)} min={0} max={0.3} step={0.005}
                    onChange={set("shock_prob")} testId="mc-p" />
                  <Field label="Shock magnitude" value={params.shock_loss}
                    display={pctDisp(params.shock_loss)} min={0} max={1} step={0.05}
                    onChange={set("shock_loss")} testId="mc-loss" last />
                </div>
                <div>
                  <SectionLabel>Simulation</SectionLabel>
                  <Field label="Monte Carlo paths" value={params.n_sims}
                    display={`${params.n_sims} sims`} min={50} max={2000} step={50}
                    onChange={(v) => set("n_sims")(Math.round(v))} testId="mc-n-sims" last />
                </div>
                <div>
                  <SectionLabel>Sensitivity grid</SectionLabel>
                  <Field label="Growth range · min" value={params.g_min}
                    display={pctDisp(params.g_min)} min={-0.05} max={0.35} step={0.005}
                    onChange={set("g_min")} testId="mc-g-min" />
                  <Field label="Growth range · max" value={params.g_max}
                    display={pctDisp(params.g_max)} min={0} max={0.4} step={0.005}
                    onChange={set("g_max")} testId="mc-g-max" />
                  <Field label="Discount range · min" value={params.r_min}
                    display={pctDisp(params.r_min)} min={0.02} max={0.2} step={0.005}
                    onChange={set("r_min")} testId="mc-r-min" />
                  <Field label="Discount range · max" value={params.r_max}
                    display={pctDisp(params.r_max)} min={0.05} max={0.3} step={0.005}
                    onChange={set("r_max")} testId="mc-r-max" last />
                </div>
              </div>
            )}
          </div>

          <button
            onClick={run}
            disabled={busy}
            data-testid="mc-run"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-primary text-primary-foreground px-6 py-4 text-xs uppercase tracking-[0.25em] hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> : <Play size={16} strokeWidth={1.5} />}
            {busy ? "Simulating…" : "Run simulation"}
          </button>
        </section>

        {/* Outputs */}
        <section className="lg:col-span-8 space-y-8">
          {!result && !busy && <EmptyState />}
          {busy && !result && (
            <div className="border border-border rounded-sm p-16 flex flex-col items-center justify-center text-foreground/60">
              <Loader2 size={22} strokeWidth={1.5} className="animate-spin mb-4" />
              <div className="text-xs uppercase tracking-widest">Running the world in parallel…</div>
            </div>
          )}
          {result && (
            <Results
              result={result}
              params={params}
              fmt={fmt}
              cs={cs}
              busy={busy}
            />
          )}
        </section>
      </div>

      <RelatedThoughtCard />

      <ToolPrimer
        title="Why a DCF should be a distribution, not a number."
        sections={[
          {
            heading: "What the tool does",
            body: "The Monte Carlo DCF values a company as the present value of its future free cash flows plus a terminal value — the same skeleton any analyst uses. What changes here is that instead of typing in one growth rate, one margin, one discount rate and getting back one 'answer,' each assumption is drawn thousands of times from a distribution you control. The output is a full histogram of intrinsic values, not a single point pretending to be truth.",
          },
          {
            heading: "How to read the output",
            body: "Focus on the P10, P50 and P90 bands. The median is your central estimate; the gap between the tails is your honest uncertainty. If the current price is below P10, you are buying with a wide margin of safety. If it is above P90, the market disagrees with your model and you should ask why. A tight distribution means the model is confident (or the volatility inputs are too low); a very wide one means the business is too hard to value at all.",
          },
          {
            heading: "The knobs that matter",
            body: "Revenue growth and EBIT margin drive early cash flows; the discount rate and terminal growth drive the terminal value, which is usually more than half the total. Terminal growth cannot exceed the long-run growth of the economy — around two to three percent — or the model becomes a perpetual-motion machine.",
          },
        ]}
        caveat="A Monte Carlo DCF is only as honest as its inputs. Fat tails in real markets are wider than a normal distribution suggests, and structural breaks (regulation, disruption, war) do not appear in any distribution you can sample. Treat the histogram as a lens on your own assumptions, not on the future."
      />
    </main>
  );
}

/* ---------------- Sub-components ---------------- */

function Panel({ title, side, children }) {
  return (
    <div className="border border-border rounded-sm p-6 bg-background">
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/70">
          {title}
        </div>
        {side}
      </div>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/40 mb-4">
      {children}
    </div>
  );
}

function Field({ label, value, display, min, max, step, onChange, testId, last }) {
  return (
    <div className={last ? "" : "mb-6"}>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[11px] uppercase tracking-[0.18em] text-foreground/70">
          {label}
        </label>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
          }}
          data-testid={`${testId}-input`}
          className="w-24 text-right bg-transparent font-serif text-base tabular-nums focus:outline-none border-b border-border focus:border-primary transition-colors"
        />
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`${testId}-slider`}
      />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-foreground/40">
        {display}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="mc-empty"
      className="border border-dashed border-border rounded-sm p-16 text-center"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">
        Awaiting the first simulation
      </div>
      <p className="mt-4 font-serif text-2xl text-foreground/70 italic max-w-md mx-auto">
        Set your assumptions on the left, then press Run.
      </p>
      <p className="mt-4 text-xs text-foreground/50 max-w-md mx-auto leading-relaxed">
        Defaults reflect the example stock from the model: price {"$"}284, EPS $8.96,
        13% expected annual EPS growth, 12% discount rate.
      </p>
    </div>
  );
}

/* ---------------- Results ---------------- */
function Results({ result, params, fmt, cs }) {
  const { summary, histogram, eps_paths, sensitivity, current_price } = result;
  const above = summary.prob_above_current;
  const verdict =
    above >= 0.9 ? { label: "Likely undervalued", tone: "text-primary" } :
    above >= 0.5 ? { label: "Fair to undervalued", tone: "text-primary/80" } :
    above >= 0.25 ? { label: "Borderline", tone: "text-accent" } :
                     { label: "Priced at or above", tone: "text-foreground/70" };

  return (
    <>
      {/* Summary hero card */}
      <div className="border border-border rounded-sm p-8 md:p-10 bg-background relative overflow-hidden">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">
              Average intrinsic price (from {summary.n_sims_effective} sims)
            </div>
            <div className="font-serif text-5xl md:text-7xl font-light tracking-tighter mt-3" data-testid="mc-avg-price">
              {fmt(summary.avg_price)}
            </div>
            <div className={`mt-3 text-xs uppercase tracking-[0.25em] ${verdict.tone}`}>
              vs current {fmt(current_price)} — {verdict.label}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">
              P(intrinsic &gt; current)
            </div>
            <div className="font-serif text-5xl md:text-6xl mt-3 text-primary tabular-nums" data-testid="mc-prob-above">
              {(above * 100).toFixed(0)}%
            </div>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-border">
          <Stat label="P/E now" value={summary.pe_current?.toFixed(1) ?? "—"} testId="mc-pe" />
          <Stat label="10th pct" value={fmt(summary.p10_price)} testId="mc-p10" />
          <Stat label="90th pct" value={fmt(summary.p90_price)} testId="mc-p90" />
          <Stat label="Terminal value share" value={pctDisp(summary.avg_tv_share)} testId="mc-tv-share" />
        </div>
      </div>

      {/* EPS paths */}
      <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="mc-eps-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
            Annual EPS · sample paths & 10–90 band
          </div>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={buildEpsData(eps_paths)} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="epsBand" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} width={50} tickFormatter={(v) => `${cs}${v.toFixed(0)}`} />
              <Tooltip content={<EpsTip cs={cs} />} />
              {/* Sample paths (light) */}
              {eps_paths.sample.map((_, i) => (
                <Line
                  key={`sample-${i}`}
                  type="monotone"
                  dataKey={`s${i}`}
                  stroke="hsl(var(--primary))"
                  strokeOpacity={0.12}
                  strokeWidth={1}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
              {/* Band (p10 to p90 via stacked area) */}
              <Area
                type="monotone"
                dataKey="p10"
                stroke="none"
                fill="transparent"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="bandWidth"
                stackId="band"
                stroke="none"
                fill="url(#epsBand)"
                isAnimationActive={false}
              />
              <Line type="monotone" dataKey="median" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Price distribution */}
      <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="mc-hist-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
            Distribution of intrinsic prices
          </div>
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-foreground/60">
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-primary/60 border border-primary" /> Simulated
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-0.5 h-3 bg-destructive" /> Current price
            </span>
          </div>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={histogram} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="x"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickFormatter={(v) => `${cs}${Math.round(v)}`}
              />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
              <Tooltip content={<HistTip cs={cs} />} />
              <Bar dataKey="count" fill="hsl(var(--primary))" fillOpacity={0.55} stroke="hsl(var(--primary))" strokeWidth={1} />
              <ReferenceLine
                x={current_price}
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{
                  value: `Current ${cs}${current_price}`,
                  fill: "hsl(var(--destructive))",
                  fontSize: 11,
                  position: "top",
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sensitivity heatmap */}
      <Heatmap
        sensitivity={sensitivity}
        current={{ g: params.growth, r: params.discount }}
      />
    </>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-foreground/50">
        {label}
      </div>
      <div className="font-serif text-2xl md:text-3xl mt-1">{value}</div>
    </div>
  );
}

function Heatmap({ sensitivity, current }) {
  const { g_range, r_range, prob_matrix } = sensitivity;
  // g on Y (rows, top = highest), r on X (cols, left = lowest)
  const rowsTopDown = [...g_range].map((_, i) => g_range.length - 1 - i);

  const cellColor = (p) => {
    if (p >= 0.9) return "bg-primary text-primary-foreground";
    if (p >= 0.75) return "bg-primary/70 text-primary-foreground";
    if (p >= 0.5) return "bg-primary/45 text-foreground";
    if (p >= 0.25) return "bg-primary/22 text-foreground";
    return "bg-secondary text-foreground/60";
  };

  // Find nearest cell to current (r, g)
  const nearest = (arr, val) =>
    arr.reduce((best, v, i) => (Math.abs(v - val) < Math.abs(arr[best] - val) ? i : best), 0);
  const activeCol = nearest(r_range, current.r);
  const activeRow = nearest(g_range, current.g);

  return (
    <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="mc-heatmap">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
            Sensitivity · P(intrinsic &gt; current)
          </div>
          <div className="text-[10px] uppercase tracking-widest text-foreground/50 mt-2">
            Rows: growth ↑ · Cols: discount →
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-foreground/60">
          <LegendSwatch cls="bg-secondary" label="< 25%" />
          <LegendSwatch cls="bg-primary/22" label="25%" />
          <LegendSwatch cls="bg-primary/45" label="50%" />
          <LegendSwatch cls="bg-primary/70" label="75%" />
          <LegendSwatch cls="bg-primary" label="90%+" />
        </div>
      </div>

      <div className="flex gap-3">
        {/* Y axis labels */}
        <div className="flex flex-col justify-between py-1">
          {rowsTopDown.map((i) => (
            <div key={i} className="text-[10px] text-foreground/50 tabular-nums text-right pr-1 h-8 flex items-center">
              {pctDisp(g_range[i], 1)}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="flex-1">
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${r_range.length}, minmax(0, 1fr))`,
            }}
          >
            {rowsTopDown.map((rowIdx) =>
              r_range.map((_, colIdx) => {
                const p = prob_matrix[rowIdx][colIdx];
                const isActive = rowIdx === activeRow && colIdx === activeCol;
                return (
                  <div
                    key={`${rowIdx}-${colIdx}`}
                    className={`h-8 rounded-sm ${cellColor(p)} flex items-center justify-center text-[10px] font-medium tabular-nums transition-colors duration-200 ${
                      isActive ? "ring-2 ring-accent ring-offset-1 ring-offset-background" : ""
                    }`}
                    title={`g=${pctDisp(g_range[rowIdx], 1)}, r=${pctDisp(r_range[colIdx], 1)} → ${(p * 100).toFixed(0)}%`}
                    data-testid={`mc-heatmap-cell-${rowIdx}-${colIdx}`}
                  >
                    {isNaN(p) ? "—" : `${Math.round(p * 100)}`}
                  </div>
                );
              })
            )}
          </div>
          {/* X axis labels */}
          <div
            className="grid gap-1 mt-2"
            style={{ gridTemplateColumns: `repeat(${r_range.length}, minmax(0, 1fr))` }}
          >
            {r_range.map((r, i) => (
              <div key={`r-${r}-${i}`} className="text-[10px] text-foreground/50 tabular-nums text-center">
                {pctDisp(r, 1)}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 text-xs text-foreground/60 leading-relaxed max-w-2xl">
        The gold-ringed cell is your current assumption ({pctDisp(current.g, 1)} growth, {pctDisp(current.r, 1)} discount).
        Each cell is the probability that the simulated intrinsic price exceeds today's market price
        under that pair.
      </div>
    </div>
  );
}

function LegendSwatch({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-3 h-3 rounded-sm border border-border ${cls}`} />
      {label}
    </span>
  );
}

/* ---------------- Related Thought callout ---------------- */
function RelatedThoughtCard() {
  return (
    <div className="mt-24 border-t border-border pt-14">
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 mb-6">
          Related thought · 28.07.2026
        </div>
        <blockquote className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/85">
          “When investing, one should have very clear the outcome distribution
          of its positions: if you don't know it, it is better leaving the table.”
        </blockquote>
        <Link
          to="/thoughts"
          data-testid="mc-related-thought"
          className="inline-block mt-8 text-xs uppercase tracking-[0.2em] text-primary hover:opacity-80 transition-opacity"
        >
          Read the full entry →
        </Link>
      </div>
    </div>
  );
}

/* ---------------- Tooltips ---------------- */
function HistTip({ active, payload, cs }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
        Bin center
      </div>
      <div className="font-serif text-lg">{cs}{p.x.toFixed(2)}</div>
      <div className="mt-1 text-xs text-foreground/70">{p.count} sims</div>
    </div>
  );
}
function EpsTip({ active, payload, label, cs }) {
  if (!active || !payload || !payload.length) return null;
  const median = payload.find((p) => p.dataKey === "median")?.value;
  const p10 = payload[0]?.payload?.p10;
  const p90 = payload[0]?.payload?.p90;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
        Year {label}
      </div>
      <div className="text-xs space-y-0.5">
        <div>Median EPS: <span className="text-primary font-medium">{cs}{median?.toFixed(2)}</span></div>
        <div className="text-foreground/60">10th–90th: {cs}{p10?.toFixed(2)} — {cs}{p90?.toFixed(2)}</div>
      </div>
    </div>
  );
}

/* ---------------- Utilities ---------------- */
function pctDisp(v, decimals = 1) {
  return `${(v * 100).toFixed(decimals)}%`;
}
function buildEpsData(eps_paths) {
  return eps_paths.years.map((year, idx) => {
    const row = {
      year,
      median: eps_paths.median[idx],
      p10: eps_paths.p10[idx],
      p90: eps_paths.p90[idx],
      bandWidth: eps_paths.p90[idx] - eps_paths.p10[idx],
    };
    eps_paths.sample.forEach((path, i) => {
      row[`s${i}`] = path[idx];
    });
    return row;
  });
}
